import { createFileRoute, Link } from '@tanstack/react-router'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { AVATARS } from '@/components/dancefloor/avatar-catalog'
import { SHARED_DANCE_NAMES } from '@/components/dancefloor/character-dances'
import { GUEST_COMMANDS, HOST_COMMANDS } from '@/components/dancefloor/command-catalog'

export const Route = createFileRoute('/dancefloor-commands')({
  component: DancefloorCommands,
})

function DancefloorCommands() {
  useDocumentTitle('Dancefloor commands')
  return <main data-testid="dancefloor-commands" className="mx-auto max-w-4xl space-y-8 px-5 py-10 text-[#eee7f2] sm:px-8">
    <header className="space-y-4">
      <h1 className="font-serif text-4xl sm:text-5xl">Dancefloor commands</h1>
      <p className="text-base leading-relaxed text-white/70">Join the stream’s Twitch chat while signed in and your avatar walks onto the floor, even if you don’t type. Leave chat and your avatar walks off when Twitch updates its presence list. Your avatar and dance choices are remembered for your next visit.</p>
      <div className="rounded-xl border border-cyber-cyan/40 bg-cyber-cyan/5 p-5">
        <p className="text-sm text-white/70">Try these in Twitch chat:</p>
        <p className="mt-2 flex flex-wrap gap-x-6 gap-y-2 font-mono text-cyber-cyan"><code>!avatar jinx</code><code>!dance 3</code><code>!wave</code></p>
      </div>
      <nav aria-label="Command page sections" className="flex flex-wrap gap-4 text-sm text-cyber-cyan">
        <a href="#commands" className="underline underline-offset-4">Guest commands</a>
        <a href="#dances" className="underline underline-offset-4">Dances</a>
        <a href="#avatars" className="underline underline-offset-4">Avatars</a>
        <a href="#host" className="underline underline-offset-4">Host commands</a>
      </nav>
    </header>

    <section id="commands" aria-label="Guest commands" className="scroll-mt-24 rounded-xl border border-border bg-card p-6">
      <h2 className="font-serif text-2xl">Guest commands</h2>
      <dl className="mt-5 grid gap-x-8 gap-y-5 sm:grid-cols-2">
        {GUEST_COMMANDS.map(({ command, description }) => <div key={command} className="min-w-0">
          <dt className="font-mono text-sm text-cyber-cyan"><code>{command}</code></dt>
          <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</dd>
        </div>)}
      </dl>
    </section>

    <section id="dances" aria-label="Dance choices" className="scroll-mt-24 rounded-xl border border-border bg-card p-6">
      <h2 className="font-serif text-2xl">Eleven dances for every avatar</h2>
      <p className="mt-2 text-sm text-muted-foreground">Send <code className="text-cyber-cyan">!dance 1</code> through <code className="text-cyber-cyan">!dance 11</code>. Dances 1–6 are shared by everyone:</p>
      <ol className="mt-4 grid list-inside list-decimal gap-2 text-sm text-white/80 sm:grid-cols-2">
        {SHARED_DANCE_NAMES.map(name => <li key={name}>{name}</li>)}
      </ol>
      <p className="mt-4 text-sm text-muted-foreground">Dances 7–9 are your character’s three signature dances. Dance 10 is Thriller groove (<code>!thriller</code>), and dance 11 is Twerk (<code>!twerk</code>). <Link to="/avatar-review" className="text-cyber-cyan underline underline-offset-4">Preview avatars and dances</Link>.</p>
    </section>

    <section id="avatars" aria-label="Avatar choices" className="scroll-mt-24 rounded-xl border border-border bg-card p-6">
      <h2 className="font-serif text-2xl">Choose your avatar</h2>
      <p className="mt-2 text-sm text-muted-foreground">Send the command beside a name. You can change your look any time.</p>
      <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {AVATARS.map(avatar => <div key={avatar.id}>
          <dt className="text-sm text-white/85">{avatar.name}</dt>
          <dd className="mt-1 font-mono text-sm text-cyber-cyan"><code>!avatar {avatar.id}</code></dd>
        </div>)}
      </dl>
    </section>

    <section id="host" aria-label="Host commands" className="scroll-mt-24 rounded-xl border border-cyber-magenta/40 bg-card p-6">
      <h2 className="font-serif text-2xl">Host / DJ commands</h2>
      <p className="mt-2 text-sm text-muted-foreground">These crowd controls work only from the channel’s broadcaster account.</p>
      <dl className="mt-5 grid gap-x-8 gap-y-5 sm:grid-cols-2">
        {HOST_COMMANDS.map(({ command, description }) => <div key={command} className="min-w-0">
          <dt className="font-mono text-sm text-cyber-magenta"><code>{command}</code></dt>
          <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</dd>
        </div>)}
      </dl>
    </section>
  </main>
}
